import { Content, Root, Trigger } from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactElement, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { useDeviceInfo } from '../../hooks/useDeviceInfo';

export type PopoutDrawerProps = {
  /** Inner contents of the drawer when it is expanded */
  children: ReactElement;

  /** Contents shown at the top of the drawer next to the toolbar */
  header: ReactElement;

  /** Label assigned to the expanded container for accessibility purposes */
  label: string;

  /** Content to display in the left side of the header */
  leftHeaderContent?: ReactElement;

  /** Event handler called when the open state of the drawer changes */
  onOpenChange: (isOpen: boolean) => void;

  /** Open state used to control whether the drawer is collapsed or expanded */
  isOpen: boolean;

  /** Buttons to display in the right side of the header */
  toolbar: ReactElement;
};

function HeaderWithToolbar({
  className,
  header,
  leftHeaderContent,
  toolbar,
}: {
  className?: string;
  header: ReactElement;
  leftHeaderContent?: ReactElement;
  toolbar: ReactElement;
}) {
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const toolbarContainerRef = useRef<HTMLDivElement>(null);
  const leftHeaderContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateWidthOfHeader = () => {
      if (
        headerContainerRef.current &&
        toolbarContainerRef.current &&
        leftHeaderContentRef.current
      ) {
        headerContainerRef.current.style.width = `calc(100% - ${
          toolbarContainerRef.current.offsetWidth +
          4 + // Additional pixel value is to separate the toolbar from the header text
          leftHeaderContentRef.current.offsetWidth
        }px)`;
        headerContainerRef.current.style.marginLeft = `${leftHeaderContentRef.current.offsetWidth}px`;
      }
    };

    updateWidthOfHeader();

    window.addEventListener('resize', updateWidthOfHeader);

    return () => {
      window.removeEventListener('resize', updateWidthOfHeader);
    };
  }, [leftHeaderContent]);

  return (
    <div className={twMerge('relative', className)}>
      <div className="absolute left-2 top-2" ref={leftHeaderContentRef}>
        {leftHeaderContent}
      </div>
      <Trigger className="w-full rounded-t-lg border-x border-t border-gray-5 bg-gray-9 p-2">
        <div
          className="overflow-hidden whitespace-nowrap"
          ref={headerContainerRef}
        >
          {header}
        </div>
      </Trigger>

      <div className="absolute right-2 top-2 flex" ref={toolbarContainerRef}>
        {toolbar}
      </div>
    </div>
  );
}

export function PopoutDrawer(props: PopoutDrawerProps) {
  const {
    children,
    header,
    label,
    leftHeaderContent,
    isOpen,
    onOpenChange,
    toolbar,
  } = props;
  const { deviceType } = useDeviceInfo();
  const isMobile = deviceType === 'mobile';

  const drawerHeader = (
    <HeaderWithToolbar
      className={twMerge(
        'w-full',
        !isMobile && !isOpen && 'min-w-[180px] max-w-[272px]'
      )}
      header={header}
      leftHeaderContent={leftHeaderContent}
      toolbar={toolbar}
    />
  );

  const content = (
    <Content
      forceMount={isMobile || undefined}
      aria-label={label}
      className={twMerge(
        'flex shrink-0 flex-col rounded-t-lg shadow-lg-down',
        isMobile
          ? 'mt-4 h-[calc(100vh-16px)] w-screen shadow-md-up'
          : 'h-[80vh] max-h-[800px] min-h-[314px] w-[512px]'
      )}
      onInteractOutside={(ev) => ev.preventDefault()} // Prevent the dialog from closing when outside is clicked
    >
      {drawerHeader}
      <div className="flex-1 overflow-auto border-x border-gray-5 bg-gray-1">
        {children}
      </div>
    </Content>
  );

  return (
    <Root modal={isMobile} onOpenChange={onOpenChange} open={isOpen}>
      {!isOpen ? drawerHeader : null}
      {isMobile ? (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="popout-content"
              animate={{ y: '-100vh' }}
              exit={{ y: '55px' }}
              initial={{ y: '55px' }}
              transition={{
                duration: 0.4,
              }}
              className="absolute h-full w-full"
            >
              {content}
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        content
      )}
    </Root>
  );
}
